import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Theme = 'light' | 'dark' | 'system'

interface ThemeState {
  theme: Theme
  actualTheme: 'light' | 'dark'
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

// Helper function to get system theme
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
  }
  return 'light'
}

// Helper function to apply theme to document.
// Wraps the class swap in a `disable-transitions` flag so every element with a
// color/border transition doesn't animate during the toggle (was causing a
// visible "wave" across the /materials route).
const applyTheme = (theme: 'light' | 'dark') => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (root.classList.contains(theme)) return

  root.classList.add('disable-transitions')
  root.classList.remove('light', 'dark')
  root.classList.add(theme)

  // Force a reflow so the disable-transitions class is committed *with* the
  // new theme class before we drop it on the next frame.
  void root.offsetHeight

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      root.classList.remove('disable-transitions')
    })
  })
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      actualTheme: 'light',

      setTheme: (theme: Theme) => {
        set({ theme })
        
        let actualTheme: 'light' | 'dark'
        if (theme === 'system') {
          actualTheme = getSystemTheme()
        } else {
          actualTheme = theme
        }
        
        set({ actualTheme })
        applyTheme(actualTheme)
      },

      toggleTheme: () => {
        const { actualTheme } = get()
        const newTheme = actualTheme === 'light' ? 'dark' : 'light'
        get().setTheme(newTheme)
      }
    }),
    {
      name: 'theme-storage',
      version: 2,
      migrate: (persistedState: unknown, _version: number) => {
        // v1 default was `theme: 'system'`. Anyone on that value carried an
        // implicit dark preference on most macOS setups. Light is now the
        // product default, so quietly retire `system` for legacy stores while
        // preserving explicit `dark` / `light` choices.
        const s = (persistedState as Partial<ThemeState>) || {}
        const t = s.theme === 'dark' || s.theme === 'light' ? s.theme : 'light'
        return { ...s, theme: t, actualTheme: t } as ThemeState
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Apply theme on hydration
          let actualTheme: 'light' | 'dark'
          if (state.theme === 'system') {
            actualTheme = getSystemTheme()
          } else {
            actualTheme = state.theme
          }
          
          state.actualTheme = actualTheme
          applyTheme(actualTheme)
          
          // Listen for system theme changes
          if (typeof window !== 'undefined' && state.theme === 'system') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
            const handleChange = (e: MediaQueryListEvent) => {
              if (state.theme === 'system') {
                const newActualTheme = e.matches ? 'dark' : 'light'
                state.actualTheme = newActualTheme
                applyTheme(newActualTheme)
              }
            }
            mediaQuery.addEventListener('change', handleChange)
          }
        }
      }
    }
  )
) 